// Диалоги промпт-бара: выбор из загруженного и сгенерированного, подтверждение
// повтора запроса, карточка элемента и его пикер. Все они висят рядом с баром и
// открываются его состоянием, поэтому собраны одним слоем — бар остаётся про то,
// что видно всегда.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ElementDetailsDialog, toElementDetailsView } from "~/entities/element";
import { PickGeneratedDialog, PickUploadedDialog } from "~/features/pick-attachment";
import { ElementPickerDialog } from "~/features/pick-generation-elements";
import ConfirmationDialog from "~/shared/ui/confirm-dialog";

import type { useChatAttachments } from "./useChatAttachments";
import type { usePromptElements } from "./usePromptElements";
import type { useRepeatPrefill } from "./useRepeatPrefill";

export interface PromptDialogsProps {
    chatId: string | null;
    attachments: ReturnType<typeof useChatAttachments>;
    elements: ReturnType<typeof usePromptElements>;
    repeatPrefill: ReturnType<typeof useRepeatPrefill>;
}

export function PromptDialogs({ chatId, attachments, elements, repeatPrefill }: PromptDialogsProps) {
    // `elements` рядом с `chat`: слот открывает карточку элемента, а её поля
    // подписаны ключами библиотеки — иначе они не резолвятся.
    const { t } = useTranslation(["chat", "elements"]);

    const viewedElementDetails = useMemo(
        () => (elements.viewedElement ? toElementDetailsView(t, elements.viewedElement) : null),
        [t, elements.viewedElement],
    );

    return (
        <>
            <PickUploadedDialog
                open={attachments.pickerOpen}
                onOpenChange={attachments.setPickerOpen}
                chatId={chatId}
                limits={attachments.pickerLimits}
                currentCounts={attachments.pickerCounts}
                onConfirm={(picked) =>
                    attachments.confirmPick(picked, attachments.setPickerOpen, attachments.pickTarget)
                }
            />
            <PickGeneratedDialog
                open={attachments.generatedPickerOpen}
                onOpenChange={attachments.setGeneratedPickerOpen}
                chatId={chatId}
                limits={attachments.pickerLimits}
                currentCounts={attachments.pickerCounts}
                onConfirm={(picked) =>
                    attachments.confirmPick(
                        picked,
                        attachments.setGeneratedPickerOpen,
                        attachments.pickTarget,
                    )
                }
            />
            <ConfirmationDialog
                isOpen={repeatPrefill.confirmOpen}
                onClose={repeatPrefill.cancel}
                onConfirm={repeatPrefill.confirm}
                title={t("prompt.repeatConfirm.title")}
                description={t("prompt.repeatConfirm.description")}
                confirmText={t("prompt.repeatConfirm.confirm")}
                cancelText={t("prompt.repeatConfirm.cancel")}
            />
            {/* Слот открывает элемент целиком — с именем, описанием и остальными
                полями, а не голую ленту картинок: лайтбокс живёт внутри детали. */}
            <ElementDetailsDialog
                view={viewedElementDetails}
                open={elements.viewedElement !== null}
                onOpenChange={(open) => {
                    if (!open) elements.closeView();
                }}
            />
            {elements.supportsElements && (
                <ElementPickerDialog
                    open={elements.pickerOpen}
                    onOpenChange={elements.setPickerOpen}
                    selectedIds={elements.selectedIds}
                    maxElements={elements.limit}
                    onConfirm={elements.setSelected}
                />
            )}
        </>
    );
}
